import { AuthAddress, AuthUserInfo } from '@app/services/auth.service';

export type ProfileDeriver = (
  info: AuthUserInfo
) => string | Record<string, string> | null;

/**
 * The webform_address control writes { address, address_2, postal_code, city,
 * postal_city }, while the profile address is { street, houseNumber, zip, city }.
 * Map between the two so prefill populates the (generated) address controls.
 */
function toAddressControlValue(a: AuthAddress | null): Record<string, string> | null {
  if (!a) return null;
  const street = [a.street, a.houseNumber].filter(Boolean).join(' ').trim();
  if (!street && !a.zip && !a.city) return null;
  return {
    address: a.street ?? '',
    address_2: a.houseNumber ?? '',
    postal_code: a.zip ?? '',
    city: a.city ?? '',
    postal_city: a.zip && a.city ? `${a.zip} ${a.city}` : '',
  };
}

/**
 * sessionStorage flag set when a user starts the "Anmelden und automatisch
 * ausfüllen" flow from a form (logged-out banner). After the login redirect
 * returns, the form consumes this flag to auto-fill once. When a user is
 * already signed in, prefill is only triggered by an explicit button click —
 * so logging in elsewhere never auto-fills (e.g. filling a form for someone
 * else).
 */
export const AUTOFILL_INTENT_KEY = 'mobi-autofill-intent';

/**
 * Enables the external-app flow when passed as `?autofill=true` (or `=1`) on a
 * webform URL. `auth` and `prefill` are supported aliases for external callers.
 * Other or missing values preserve the editor's normal login-card flow.
 */
export const AUTOFILL_URL_PARAM = 'autofill';
export const AUTOFILL_URL_PARAM_ALIASES = ['auth', 'prefill'];

/**
 * Maps webform field machine names to a value from the Mobiliar ID profile.
 * Keyed by EXACT name: `name` = givenName but `leadapi_pd_name` = familyName,
 * so never strip the `leadapi_pd_` prefix. Only existing controls are patched.
 */
export const PROFILE_FIELD_MAP: Record<string, ProfileDeriver> = {
  gender: (info) => info.salutation,
  anrede: (info) => info.salutation,
  leadapi_pd_anrede: (info) => info.salutation,

  name: (info) => info.givenName,
  vorname: (info) => info.givenName,
  firstname: (info) => info.givenName,
  leadapi_pd_vorname: (info) => info.givenName,

  surname: (info) => info.familyName,
  lastname: (info) => info.familyName,
  leadapi_pd_name: (info) => info.familyName,
  leadapi_ud_name1: (info) => info.familyName,
  leadapi_ud_name: (info) => info.familyName,

  // e_mail / e_mail_adresse are transitional (PO will rename them to `email`);
  // kept because email is the one claim with live data today.
  email: (info) => info.email,
  e_mail: (info) => info.email,
  e_mail_adresse: (info) => info.email,
  leadapi_pd_email: (info) => info.email,
  leadapi_ud_email: (info) => info.email,

  phone: (info) => info.phone || info.mobilePhone,
  telefon: (info) => info.phone || info.mobilePhone,
  leadapi_pd_telefon_privat: (info) => info.phone || info.mobilePhone,

  mobile_nr: (info) => info.mobilePhone,

  birth_date: (info) => info.birthdate,
  geburtsdatum: (info) => info.birthdate,
  leadapi_pd_geburtsdatum: (info) => info.birthdate,
  leadapi_ud_gruendungsdatum: (info) => info.birthdate,

  rechtsform: (info) => info.legalFormCode,
  adapi_ud_rechtsform: (info) => info.legalFormCode,
  leadapi_ud_rechtsform: (info) => info.legalFormCode,
  leadapi_ud_telefon_geschaeftlich: (info) => info.phone,

  adress: (info) => toAddressControlValue(info.address),
  adresse: (info) => toAddressControlValue(info.address),
  leadapi_pd_address: (info) => toAddressControlValue(info.address),
  leadapi_ud_address: (info) => toAddressControlValue(info.address),

  language: (info) => info.correspondenceLanguage,
  leadapi_pd_korrespondenz_sprache: (info) => info.correspondenceLanguage,

  leadapi_pd_nationalitaet_iso3166num: (info) => info.nationality,
  leadapi_ud_nationalitaet_iso3166num: (info) => info.nationality,

  zivilstand: (info) => info.maritalStatus,
  leadapi_pd_zivilstand: (info) => info.maritalStatus,
};
